/**
 * ThriftLinkConfirmPage
 * Confirmation page for the THRIFT-initiated account linking flow.
 *
 * URL: /link-thrift?token=...&callback=...
 *
 * Shows the THRIFT user info from the token and allows the SIFT user
 * to confirm the link. On success, redirects back to THRIFT.
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ThriftLinkService } from '../services/ThriftLinkService';
import { useTutorial } from '../components/TutorialProvider';
import LoadingSpinner from '../components/LoadingSpinner';
import './ThriftLinkConfirmPage.css';

interface TokenInfo {
  valid: boolean;
  already_linked: boolean;
  thrift_email: string;
  thrift_display_name: string;
}

function ThriftLinkConfirmPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const callback = searchParams.get('callback') || '';

  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [thriftLinkService] = useState(() => new ThriftLinkService());
  const { stopTutorial, state: tutorialState } = useTutorial();

  // Kill any running tutorial immediately — it would interfere with this page
  useEffect(() => {
    if (tutorialState.isRunning) {
      console.log('[ThriftLinkConfirmPage] Stopping active tutorial to prevent interference');
      stopTutorial();
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setError('No link token provided. Please start the linking process from THRIFT.');
      setIsLoading(false);
      return;
    }
    loadTokenInfo();
  }, [token]);

  const loadTokenInfo = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const info = await thriftLinkService.getReverseTokenInfo(token);
      setTokenInfo(info);

      if (info.already_linked) {
        setError('Your SIFT account is already linked to a THRIFT account. Unlink from your Account page first if you want to link a different account.');
      } else if (!info.valid) {
        setError('This link token has expired or is invalid. Please start the linking process again from THRIFT.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load token info. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    setIsConfirming(true);
    setError(null);
    try {
      const result = await thriftLinkService.completeFromThrift(token);

      // Redirect back to THRIFT
      const redirectUrl = result.callback_url || callback;
      if (redirectUrl) {
        const sep = redirectUrl.includes('?') ? '&' : '?';
        window.location.href = `${redirectUrl}${sep}sift_linked=true`;
      } else {
        // Fallback: go to SIFT account page
        window.location.href = '/account?thrift_linked=true';
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to complete link.';
      if (msg.includes('already linked') || msg.includes('409')) {
        setError('One of the accounts is already linked. Please unlink first and try again.');
      } else if (msg.includes('expired') || msg.includes('invalid')) {
        setError('The link token has expired. Please start the process again from THRIFT.');
      } else {
        setError(msg);
      }
      setIsConfirming(false);
    }
  };

  const handleCancel = () => {
    if (callback) {
      window.location.href = callback;
    } else {
      window.location.href = '/account';
    }
  };

  if (isLoading) {
    return (
      <div className="thrift-link-confirm">
        <div className="thrift-link-confirm__card">
          <LoadingSpinner size="large" message="Loading link details..." />
        </div>
      </div>
    );
  }

  const canConfirm = tokenInfo?.valid && !tokenInfo?.already_linked;

  return (
    <div className="thrift-link-confirm">
      <div className="thrift-link-confirm__card">
        <h1 className="thrift-link-confirm__title">Link SIFT with THRIFT</h1>

        {error && (
          <div className="thrift-link-confirm__error">
            {error}
          </div>
        )}

        {canConfirm && (
          <>
            <p className="thrift-link-confirm__description">
              Link your SIFT account with the following THRIFT account:
            </p>

            <div className="thrift-link-confirm__user-info">
              {tokenInfo?.thrift_display_name && (
                <div className="thrift-link-confirm__field">
                  <span className="thrift-link-confirm__label">Name</span>
                  <span className="thrift-link-confirm__value">{tokenInfo.thrift_display_name}</span>
                </div>
              )}
              {tokenInfo?.thrift_email && (
                <div className="thrift-link-confirm__field">
                  <span className="thrift-link-confirm__label">Email</span>
                  <span className="thrift-link-confirm__value">{tokenInfo.thrift_email}</span>
                </div>
              )}
            </div>

            <p className="thrift-link-confirm__note">
              This will allow THRIFT to use your SIFT credits for AI analysis features.
            </p>
          </>
        )}

        <div className="thrift-link-confirm__actions">
          {canConfirm && (
            <button
              className="thrift-link-confirm__btn thrift-link-confirm__btn--confirm"
              onClick={handleConfirm}
              disabled={isConfirming}
            >
              {isConfirming ? 'Linking...' : 'Confirm Link'}
            </button>
          )}
          <button
            className="thrift-link-confirm__btn thrift-link-confirm__btn--cancel"
            onClick={handleCancel}
          >
            {canConfirm ? 'Cancel' : 'Go Back'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ThriftLinkConfirmPage;
